import React, { useState, useRef } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { Colors } from "../../Commons/Colors/Colors";
import { Paragraph } from "../../Commons/Paragraph";
import ArrowIcon from "../../../assets/imgs/chevron_right_trede_analyzer.svg";
import Null_player from "../../../assets/imgs/null-player-img.svg";
import polygon from "../../../assets/imgs/polygon.svg";
import removeIcon from "../../../assets/imgs/remove-icon.svg";

import {
  SimilarPlayersTeamColor,
  teamColors,
} from "../../../config/constants/teamColors";
import { hapticsImpactLight } from "../../../lib/utils";

// Define PanInfo type inline since it's no longer exported
type PanInfo = {
  point: { x: number; y: number };
  delta: { x: number; y: number };
  offset: { x: number; y: number };
  velocity: { x: number; y: number };
};

type TradeAnalyzerPlayersCardProps = {
  playerSmallIcon: string;
  playerName: string;
  playerPosition: string;
  playerStatus: string;
  bgColor: string;
  cardBg?: boolean;
  similarPlayersBg?: string;
  onClick?: () => void;
  onDelete?: () => void;
  playerId?: string;
  isSelected?: boolean;
};


const TradeAnalyzerPlayersCard: React.FC<TradeAnalyzerPlayersCardProps> = ({
  playerSmallIcon,
  playerName,
  playerPosition,
  playerStatus,
  bgColor,
  cardBg,
  similarPlayersBg,
  onClick,
  onDelete,
  playerId,
  isSelected = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isDeleteZone, setIsDeleteZone] = useState(false);
  const [hasTriggeredHaptic, setHasTriggeredHaptic] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  
  // Motion values for smooth animations
  const x = useMotionValue(0);
  const opacity = useTransform(x, [-300, -100, 0], [0.3, 0.8, 1]);
  const scale = useTransform(x, [-300, -100, 0], [0.95, 0.98, 1]);
  const deleteIconScale = useTransform(x, [-300, -150, -50, 0], [1.2, 1, 0.8, 0]);
  const deleteIconOpacity = useTransform(x, [-300, -100, -50, 0], [1, 1, 0.5, 0]);
  const deleteBackgroundOpacity = useTransform(x, [-300, -100, -50, 0], [1, 0.8, 0.3, 0]);

  // Enhanced thresholds for better UX
  const DELETE_THRESHOLD = 0.25; // 25% of card width
  const HAPTIC_THRESHOLD = 0.2; // 20% of card width for haptic feedback
  
  const formatPlayerStatus = (status: string) => {
    if (status === undefined) return "";
    if (status === null) return "";
    return status.replace(/!/g, "").trim();
  };

  const handleDragStart = () => {
    setIsDragging(true);
    setHasTriggeredHaptic(false);
  };

  const handleDrag = (event: any, info: PanInfo) => {
    const offset = info.offset.x;
    const cardWidth = cardRef.current?.offsetWidth || 300;
    const deleteThreshold = -cardWidth * DELETE_THRESHOLD;
    const hapticThreshold = -cardWidth * HAPTIC_THRESHOLD;
    
    // Update motion value for smooth animations
    x.set(offset);
    
    // Check if we're in delete zone
    const inDeleteZone = offset <= deleteThreshold;
    
    if (inDeleteZone !== isDeleteZone) {
      setIsDeleteZone(inDeleteZone);
    }
    
    // Trigger haptic feedback when entering haptic zone
    if (offset <= hapticThreshold && !hasTriggeredHaptic) {
      hapticsImpactLight();
      setHasTriggeredHaptic(true);
    } else if (offset > hapticThreshold && hasTriggeredHaptic) {
      setHasTriggeredHaptic(false);
    }
  };

  const handleDragEnd = (event: any, info: PanInfo) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    const cardWidth = cardRef.current?.offsetWidth || 300;
    const deleteThreshold = -cardWidth * DELETE_THRESHOLD;
    
    // Enhanced delete logic with velocity consideration
    const shouldDelete = offset <= deleteThreshold || 
                        (velocity <= -800 && offset <= -50); // Faster swipe with less distance
    
    if (shouldDelete && onDelete) {
      // Animate card out before deletion
      x.set(-cardWidth * 1.2);
      setTimeout(() => {
        hapticsImpactLight();
        onDelete();
      }, 150);
    } else {
      // Smooth spring back to original position
      x.set(0);
    }
    
    // Reset states
    setIsDragging(false);
    setIsDeleteZone(false);
    setHasTriggeredHaptic(false);
  };

  const handleClick = () => {
    // Only trigger click if not dragging and drag distance is minimal
    if (!isDragging && Math.abs(x.get()) < 5 && onClick) {
      onClick();
    }
  };

  // Conditional drag props - only enable drag when onDelete is provided
  const dragProps = onDelete ? {
    drag: "x" as const,
    dragConstraints: { left: -400, right: 0 },
    dragElastic: { left: 0.05, right: 0 }, // Reduced elastic for snappier feel
    dragMomentum: false, // Disable momentum for more control
    onDragStart: handleDragStart,
    onDrag: handleDrag,
    onDragEnd: handleDragEnd,
    style: { x },
  } : {};

  return (
    <div className="relative overflow-hidden" ref={cardRef}>
      {/* Enhanced Delete Background */}
      {onDelete && (
        <motion.div
          className="absolute inset-0 flex items-center justify-end pr-6 rounded-md"
          style={{
            background: "linear-gradient(90deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.3) 100%)",
            opacity: deleteBackgroundOpacity,
          }}
        >
          <motion.div
            className="flex items-center text-red-500"
            style={{
              scale: deleteIconScale,
              opacity: deleteIconOpacity,
            }}
          >
            <motion.img 
              src={removeIcon} 
              alt="removeIcon"
              animate={{
                rotate: isDeleteZone ? [0, -10, 10, 0] : 0,
              }}
              transition={{
                duration: 0.3,
                ease: "easeInOut",
              }}
            />
          </motion.div>
        </motion.div>
      )}

      {/* Main Card with Enhanced Animations */}
      <motion.div
        className="relative z-10 cursor-pointer"
        style={{
          background: isSelected 
            ? "rgba(255, 255, 255, 0.1)" 
            : (!cardBg ? "transparent" : Colors.backgroundSecundary),
          opacity,
          scale,
          padding : "8px 20px"
        }}
        {...dragProps}
        onClick={handleClick}
        whileTap={{ scale: 0.98 }}
        transition={{
          type: "spring",
          stiffness: 400,
          damping: 40,
          mass: 0.8,
        }}
      >
        <div className="flex items-center justify-between w-full">
          <div className="flex gap-4 items-center">
            <div className="relative w-[54px] h-[47.84px]">
              <motion.div
                className="rounded-xl w-[44px] h-[44px] flex items-end overflow-hidden"
                style={{
                  backgroundColor: bgColor
                    ? teamColors[bgColor]
                    : similarPlayersBg
                      ? SimilarPlayersTeamColor[similarPlayersBg]
                      : "#4F2683",
                }}
                animate={{
                  scale: isDeleteZone ? 0.9 : 1,
                }}
                transition={{ duration: 0.2 }}
              >
                <img
                  className={playerSmallIcon ? " scale-[1.7]" : "h-[45px]"}
                  src={playerSmallIcon || Null_player}
                  alt=""
                />
              </motion.div>
              <div className="absolute bottom-[-1px] right-[-1px]">
                <div className="relative">
                  <img
                    src={polygon}
                    alt=""
                    className="w-[23px] h-[23px]"
                  />
                  <Paragraph
                    text={playerPosition}
                    props={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                    className="text-[8px] font-normal"
                    ParagraphType="Pragragh22"
                    color="textSecundary"
                  />
                </div>
              </div>
            </div>
            <div className="flex-1">
              <motion.div
                animate={{
                  opacity: isDeleteZone ? 0.6 : 1,
                }}
                transition={{ duration: 0.2 }}
              >
                <Paragraph
                  text={playerName}
                  ParagraphType="Pragragh22"
                  color="textPrimary"
                  className="text-[14px] font-medium"
                />
                <Paragraph
                  text={formatPlayerStatus(playerStatus)}
                  props={{
                    fontSize: "10px",
                    marginTop: "2px",
                    opacity: 0.7,
                  }}
                  ParagraphType="Pragragh22"
                  color="textPrimary"
                  className="text-[10px] text-white/50 font-medium"
                />
              </motion.div>
            </div>
          </div>
          <motion.img
            src={ArrowIcon}
            alt="ArrowIcon"
            className="ml-4"
            animate={{
              opacity: isDragging ? 0.2 : 1,
              x: isDragging ? -15 : 0,
              rotate: isDeleteZone ? 180 : 0,
            }}
            transition={{ 
              duration: 0.3,
              ease: "easeOut",
            }}
          />
        </div>
      </motion.div>
    </div>
  );
};

export default TradeAnalyzerPlayersCard;
